import { NavLink, Stack, ActionIcon, Tooltip, Box, ScrollArea, Divider, Badge } from '@mantine/core';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@/features/auth/store/authStore';
import { usePendingAssignmentsCount } from '@/features/student/hooks/useAssignments';
import {
  IconHome,
  IconBook,
  IconCards,
  IconChartBar,
  IconClipboardList,
  IconUsers,
  IconMenu2,
  IconSchool,
  IconUsersGroup,
  IconPalette,
  IconShieldCheck,
  IconMoodKid,
  IconListCheck,
  IconMailForward,
  IconBookmark,
} from '@tabler/icons-react';

export type NavbarState = 'expanded' | 'collapsed';

interface NavbarProps {
  state: NavbarState;
  onStateChange: (state: NavbarState) => void;
}

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: number;
  roles?: string[];
  children?: NavItem[];
}

export function Navbar({ state, onStateChange }: NavbarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((state) => state.user);
  const isStudent = user?.roles?.includes('student');
  const isParent = user?.roles?.includes('parent');
  const { data: pendingCount } = usePendingAssignmentsCount({ enabled: isStudent });

  const navItems: NavItem[] = [
    // Student Home
    {
      label: 'Home',
      icon: <IconHome size={20} stroke={1.5} />,
      path: '/',
      roles: ['student'],
    },
    // Parent Dashboard
    {
      label: 'Dashboard',
      icon: <IconHome size={20} stroke={1.5} />,
      path: '/parent',
      roles: ['parent'],
    },
    // Parent Children
    {
      label: 'My Children',
      icon: <IconMoodKid size={20} stroke={1.5} />,
      path: '/parent/children',
      roles: ['parent'],
    },
    // Assignments (different paths for parent vs student)
    {
      label: 'Assignments',
      icon: <IconClipboardList size={20} stroke={1.5} />,
      path: isParent ? '/parent/assignments' : '/assignments',
      badge: isStudent ? (pendingCount?.pending_count || undefined) : undefined,
      roles: ['student', 'parent'],
    },
    // Flashcards (shared)
    {
      label: 'Flashcards',
      icon: <IconCards size={20} stroke={1.5} />,
      path: '/flashcards',
      roles: ['student', 'parent'],
    },
    // Library (shared)
    {
      label: 'Library',
      icon: <IconBook size={20} stroke={1.5} />,
      path: '/library',
      roles: ['student', 'parent'],
    },
    // Student Progress
    {
      label: 'Progress',
      icon: <IconChartBar size={20} stroke={1.5} />,
      path: '/analytics',
      roles: ['student'],
    },
  ];

  const adminItems: NavItem[] = [
    {
      label: 'Users',
      icon: <IconUsersGroup size={20} stroke={1.5} />,
      path: '/admin/users',
      roles: ['admin'],
    },
    {
      label: 'Students',
      icon: <IconSchool size={20} stroke={1.5} />,
      path: '/admin/students',
      roles: ['admin'],
    },
    {
      label: 'Parents',
      icon: <IconUsers size={20} stroke={1.5} />,
      path: '/admin/parents',
      roles: ['admin'],
    },
    {
      label: 'Themes',
      icon: <IconPalette size={20} stroke={1.5} />,
      path: '/admin/themes',
      roles: ['admin'],
    },
    {
      label: 'Stories',
      icon: <IconBookmark size={20} stroke={1.5} />,
      path: '/admin/stories',
      roles: ['admin'],
    },
    {
      label: 'Jobs',
      icon: <IconListCheck size={20} stroke={1.5} />,
      path: '/admin/jobs',
      roles: ['admin'],
    },
    {
      label: 'Invitations',
      icon: <IconMailForward size={20} stroke={1.5} />,
      path: '/admin/invites',
      roles: ['admin'],
    },
  ];

  const hasRole = (roles?: string[]) => {
    if (!roles || roles.length === 0) return true;
    return roles.some(role => user?.roles?.includes(role as 'student' | 'parent' | 'teacher' | 'admin'));
  };

  const renderNavItem = (item: NavItem) => {
    if (!hasRole(item.roles)) return null;

    // Use exact matching for root paths and dashboard to prevent false positives
    const isActive = item.path === '/' || item.path === '/parent'
      ? location.pathname === item.path
      : location.pathname.startsWith(item.path);

    if (state === 'collapsed') {
      return (
        <Tooltip key={item.path} label={item.label} position="right" withArrow>
          <Box style={{ position: 'relative' }}>
            <NavLink
              onClick={() => navigate(item.path)}
              active={isActive}
              label=""
              leftSection={item.icon}
              styles={(theme) => ({
                root: {
                  borderRadius: theme.radius.md,
                  padding: '10px',
                  color: isActive ? theme.colors.teal[9] : theme.other.sidebarText,
                  backgroundColor: isActive ? theme.colors.teal[2] : 'transparent',
                  fontWeight: isActive ? 600 : 400,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isActive ? theme.colors.teal[2] : theme.colors.teal[1],
                    color: isActive ? theme.colors.teal[9] : theme.colors.teal[8],
                    transform: 'translateX(2px)',
                  },
                },
              })}
            />
            {item.badge && item.badge > 0 && (
              <Badge
                color="red"
                variant="filled"
                size="xs"
                circle
                style={{
                  position: 'absolute',
                  top: '4px',
                  right: '4px',
                  minWidth: '16px',
                  height: '16px',
                  padding: 0,
                }}
              >
                {item.badge > 9 ? '9+' : item.badge}
              </Badge>
            )}
          </Box>
        </Tooltip>
      );
    }

    return (
      <Box key={item.path}>
        <NavLink
          onClick={() => navigate(item.path)}
          active={isActive}
          label={item.label}
          leftSection={item.icon}
          rightSection={
            item.badge && item.badge > 0 ? (
              <Badge
                color="red"
                variant="filled"
                size="sm"
                styles={{
                  root: {
                    minWidth: '20px',
                    height: '20px',
                    padding: '0 8px',
                  },
                }}
              >
                {item.badge}
              </Badge>
            ) : undefined
          }
          styles={(theme) => ({
            root: {
              borderRadius: theme.radius.md,
              color: isActive ? theme.colors.teal[9] : theme.other.sidebarText,
              backgroundColor: isActive ? theme.colors.teal[2] : 'transparent',
              fontWeight: isActive ? 600 : 400,
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: isActive ? theme.colors.teal[2] : theme.colors.teal[1],
                color: isActive ? theme.colors.teal[9] : theme.colors.teal[8],
                transform: 'translateX(2px)',
              },
            },
            label: {
              color: 'inherit',
              fontWeight: 'inherit',
            },
          })}
        />
      </Box>
    );
  };

  const toggleState = () => {
    if (state === 'expanded') {
      onStateChange('collapsed');
    } else {
      onStateChange('expanded');
    }
  };

  return (
    <Stack
      gap={0}
      styles={(theme) => ({
        root: {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: theme.other.sidebarBg,
        },
      })}
    >
      <ScrollArea styles={{ root: { flex: 1 } }} p="md">
        <Stack gap="xs">
          {/* Toggle Button */}
          <Tooltip label={state === 'expanded' ? 'Collapse to icons' : 'Expand navigation'} position="right" withArrow>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={toggleState}
              styles={(theme) => ({
                root: {
                  color: theme.other.sidebarText,
                  borderRadius: theme.radius.md,
                  backgroundColor: 'transparent',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: theme.colors.teal[2],
                    transform: 'translateX(1px)',
                  },
                },
              })}
            >
              <IconMenu2 size={20} stroke={1.5} />
            </ActionIcon>
          </Tooltip>

          {/* Main Navigation */}
          {navItems.map(renderNavItem)}

          {/* Admin Section */}
          {user?.roles?.includes('admin') && (
            <>
              <Divider
                my="sm"
                label={state === 'expanded' ? 'Admin' : undefined}
                labelPosition="center"
                styles={(theme) => ({
                  root: {
                    borderColor: theme.other.sidebarBorder,
                  },
                  label: {
                    color: theme.other.sidebarText,
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    textTransform: 'uppercase',
                  },
                })}
              />
              {adminItems.map(renderNavItem)}
            </>
          )}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}
